import { Alert, Button, Col, Form, InputNumber, Row, Select, Switch, Tag, Typography } from "antd";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

const { Option } = Select;
const { Title } = Typography;
const MAX_CHANNEL_VALUE = 10_000;
const MAX_INTEGER = 2_147_483_647;

export type PulseValidationIssue = {
  id: string;
  values?: { channelNumber: number };
};

export const validatePulseModule = (pulseModule: PulseModule): PulseValidationIssue[] => {
  const issues: PulseValidationIssue[] = [];

  if (!pulseModule.pulseModuleType) {
    issues.push({ id: "vendingmachines.pulse.validation.moduleType" });
  }
  if (!pulseModule.channels || pulseModule.channels.length === 0) {
    issues.push({ id: "vendingmachines.pulse.validation.channels" });
    return issues;
  }
  if (!pulseModule.channels.some((channel) => channel.active)) {
    issues.push({ id: "vendingmachines.pulse.validation.activeChannel" });
  }

  pulseModule.channels.forEach((channel) => {
    const values = { channelNumber: channel.channelNumber };
    if (!channel.coinValue || channel.coinValue < 1 || channel.coinValue > MAX_CHANNEL_VALUE) {
      issues.push({ id: "vendingmachines.pulse.validation.coinValue", values });
    }
    if (!channel.price || channel.price < 1 || channel.price > MAX_CHANNEL_VALUE) {
      issues.push({ id: "vendingmachines.pulse.validation.price", values });
    }
    if (!channel.injectionFrequency || channel.injectionFrequency < 1) {
      issues.push({ id: "vendingmachines.pulse.validation.injectionFrequency", values });
    }
    if (!channel.pulseLengthMs || channel.pulseLengthMs < 1) {
      issues.push({ id: "vendingmachines.pulse.validation.pulseLength", values });
    }
    if (!channel.pauseLengthMs || channel.pauseLengthMs < 1) {
      issues.push({ id: "vendingmachines.pulse.validation.pauseLength", values });
    }
  });

  const fixedPriceChannel = pulseModule.channels
    .filter((channel) => channel.active)
    .sort((left, right) => left.channelNumber - right.channelNumber)[0];
  if (
    pulseModule.fixedPriceEnabled &&
    fixedPriceChannel?.price &&
    fixedPriceChannel?.coinValue &&
    fixedPriceChannel.price % fixedPriceChannel.coinValue !== 0
  ) {
    issues.push({ id: "vendingmachines.pulse.validation.priceMultiple" });
  }

  if (pulseModule.pulseModuleType === "2" && !pulseModule.blockingLine?.status) {
    issues.push({ id: "vendingmachines.pulse.validation.blockingLine" });
  }

  return issues;
};

type PulseModuleSettingsProps = {
  pulseModule: PulseModule;
  disabled: boolean;
  // Fixpreis nur bei Münzeinwurf (SLOT). Bei CARWASH liefert Svenny (externes System) den Preis.
  allowFixedPrice: boolean;
  onModuleChange: (field: keyof PulseModule, value: PulseModule[keyof PulseModule]) => void;
  onChannelChange: (channelNumber: number, value: Partial<PulseChannel>) => void;
  onBlockingLineChange: (value: Partial<PulseBlockingLine>) => void;
  onSave: () => void;
};

const optionalNumber = (value: number | string | null | undefined): number | undefined => {
  return value === null || value === undefined || value === "" ? undefined : Number(value);
};

const PulseModuleSettings = ({
  pulseModule,
  disabled,
  allowFixedPrice,
  onModuleChange,
  onChannelChange,
  onBlockingLineChange,
  onSave,
}: PulseModuleSettingsProps) => {
  const intl = useIntl();
  const validationIssues = validatePulseModule(pulseModule);
  const fixedPriceChannelNumber = pulseModule.channels
    .filter((channel) => channel.active)
    .sort((left, right) => left.channelNumber - right.channelNumber)[0]?.channelNumber;

  return (
    <>
      <Title level={4} style={{ paddingBottom: "16px" }}>
        <FormattedMessage id="vendingmachines.title.pulseSettings" />
      </Title>
      {validationIssues.length > 0 ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: "16px" }}
          message={intl.formatMessage({ id: "common.message.required" })}
          description={validationIssues.map((issue) => (
            <div key={`${issue.id}-${issue.values?.channelNumber || "module"}`}>
              {intl.formatMessage({ id: issue.id }, issue.values)}
            </div>
          ))}
        />
      ) : null}
      <Form layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label={<FormattedMessage id="vendingmachines.pulse.moduleType" />} required>
              <Select
                value={pulseModule.pulseModuleType}
                onChange={(value) => onModuleChange("pulseModuleType", value)}
                disabled={disabled}
              >
                <Option value="1">V1</Option>
                <Option value="2">V2</Option>
              </Select>
            </Form.Item>
          </Col>
          {allowFixedPrice ? (
            <Col xs={24} md={8}>
              <Form.Item label={<FormattedMessage id="vendingmachines.pulse.fixedPriceEnabled" />}>
                <Switch
                  checked={Boolean(pulseModule.fixedPriceEnabled)}
                  onChange={(checked) => onModuleChange("fixedPriceEnabled", checked)}
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
          ) : null}
        </Row>

        {pulseModule.channels.map((channel) => (
          <div
            key={channel.channelNumber}
            style={{ borderTop: "1px solid #f0f0f0", marginBottom: "8px", paddingTop: "16px" }}
          >
            <Title level={5}>
              <FormattedMessage id="vendingmachines.pulse.channel" values={{ channelNumber: channel.channelNumber }} />
              {allowFixedPrice && pulseModule.fixedPriceEnabled && fixedPriceChannelNumber === channel.channelNumber ? (
                <Tag color="blue" style={{ marginLeft: "12px" }}>
                  <FormattedMessage id="vendingmachines.pulse.fixedPriceChannel" />
                </Tag>
              ) : null}
            </Title>
            <Row gutter={16}>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<FormattedMessage id="vendingmachines.pulse.active" />}>
                  <Switch
                    checked={Boolean(channel.active)}
                    onChange={(checked) => onChannelChange(channel.channelNumber, { active: checked })}
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<FormattedMessage id="vendingmachines.pulse.coinValue" />} required>
                  <InputNumber
                    min={1}
                    max={MAX_CHANNEL_VALUE}
                    value={channel.coinValue}
                    onChange={(value) => onChannelChange(channel.channelNumber, { coinValue: optionalNumber(value) })}
                    disabled={disabled}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<FormattedMessage id="vendingmachines.pulse.price" />} required>
                  <InputNumber
                    min={1}
                    max={MAX_CHANNEL_VALUE}
                    value={channel.price}
                    onChange={(value) => onChannelChange(channel.channelNumber, { price: optionalNumber(value) })}
                    disabled={disabled}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<FormattedMessage id="vendingmachines.pulse.injectionFrequency" />} required>
                  <InputNumber
                    min={1}
                    max={MAX_INTEGER}
                    value={channel.injectionFrequency}
                    onChange={(value) =>
                      onChannelChange(channel.channelNumber, { injectionFrequency: optionalNumber(value) })
                    }
                    disabled={disabled}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<FormattedMessage id="vendingmachines.pulse.pulseLengthMs" />} required>
                  <InputNumber
                    min={1}
                    max={MAX_INTEGER}
                    value={channel.pulseLengthMs}
                    onChange={(value) => onChannelChange(channel.channelNumber, { pulseLengthMs: optionalNumber(value) })}
                    disabled={disabled}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<FormattedMessage id="vendingmachines.pulse.pauseLengthMs" />} required>
                  <InputNumber
                    min={1}
                    max={MAX_INTEGER}
                    value={channel.pauseLengthMs}
                    onChange={(value) => onChannelChange(channel.channelNumber, { pauseLengthMs: optionalNumber(value) })}
                    disabled={disabled}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<FormattedMessage id="vendingmachines.pulse.polarity" />} required>
                  <Select
                    value={channel.polarity}
                    onChange={(value) => onChannelChange(channel.channelNumber, { polarity: value })}
                    disabled={disabled}
                  >
                    <Option value={0}>0</Option>
                    <Option value={1}>1</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>
        ))}

        {pulseModule.pulseModuleType === "2" ? (
          <div style={{ borderTop: "1px solid #f0f0f0", marginBottom: "16px", paddingTop: "16px" }}>
            <Title level={5}>
              <FormattedMessage id="vendingmachines.pulse.blockingLine" />
            </Title>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label={<FormattedMessage id="vendingmachines.pulse.blockingLineActive" />}>
                  <Switch
                    checked={Boolean(pulseModule.blockingLine?.active)}
                    onChange={(checked) => onBlockingLineChange({ active: checked })}
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label={<FormattedMessage id="vendingmachines.pulse.blockingLineStatus" />} required>
                  <Select
                    value={pulseModule.blockingLine?.status}
                    onChange={(value) => onBlockingLineChange({ status: value })}
                    disabled={disabled}
                  >
                    <Option value="HIGH">HIGH</Option>
                    <Option value="LOW">LOW</Option>
                    <Option value="OPEN">OPEN</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>
        ) : null}

        <Button type="primary" onClick={onSave} disabled={disabled || validationIssues.length > 0}>
          <FormattedMessage id="coupon.button.save" />
        </Button>
      </Form>
    </>
  );
};

export default PulseModuleSettings;
